import { Routes, Route } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider } from '@/contexts/AuthContext';
import { CartProvider } from '@/contexts/CartContext';
import Index from '@/pages/Index';
import DisplaysOverview from '@/pages/DisplaysOverview';
import DisplaysMap from '@/pages/DisplaysMap';
import DisplayDetail from '@/pages/DisplayDetail';
import EditDisplay from '@/pages/EditDisplay';
import OwnerProfile from '@/pages/OwnerProfile';
import Sequences from '@/pages/Sequences';
import SequencesInstantSearch from '@/pages/SequencesInstantSearch';
import DisplaysInstantSearch from '@/pages/DisplaysInstantSearch';
import UploadSequencePage from '@/pages/UploadSequence';
import Search from '@/pages/Search';
import SequenceDetail from '@/pages/SequenceDetail';
import SongDetail from '@/pages/SongDetail';
import Categories from '@/pages/Categories';
import CategoryDetail from '@/pages/CategoryDetail';
import UserProfile from '@/pages/UserProfile';
import NotFound from '@/pages/NotFound';
import Favorites from '@/pages/Favorites';
import Auth from '@/pages/Auth';
import ForgotPassword from '@/pages/ForgotPassword';
import CustomSignUp from '@/pages/CustomSignUp';
import VerifyEmail from '@/pages/VerifyEmail';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import TermsOfService from '@/pages/TermsOfService';
import AboutUs from '@/pages/AboutUs';
import Contact from '@/pages/Contact';
import GettingStarted from '@/pages/GettingStarted';
import FAQ from '@/pages/FAQ';
import DisplayGuide from '@/pages/DisplayGuide';
import SequenceGuide from '@/pages/SequenceGuide';
import { AuthCallback } from '@/components/auth/AuthCallback';
import WelcomeModal from '@/components/auth/WelcomeModal';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useThemeEffect } from '@/hooks/useThemeEffect';
import RatingTest from './components/test/RatingTest';
import GenresPage from '@/pages/sequences/Genres';
import AdminPage from '@/pages/Admin';
import DatabaseDebugger from '@/components/debug/DatabaseDebugger';
import MigrationRunner from '@/components/debug/MigrationRunner';
import MyDownloads from '@/pages/MyDownloads';
import MySequences from '@/pages/MySequences';
import EditSequence from '@/pages/EditSequence';
import Videos from '@/pages/Videos';
import VideoDetail from '@/pages/VideoDetail';
import VideoUpload from '@/pages/VideoUpload';
import DownloadTestComponent from '@/components/testing/DownloadTestComponent';
import PurchaseSuccess from '@/pages/PurchaseSuccess';
import SellerDashboard from '@/pages/SellerDashboard';
import Feedback from '@/pages/Feedback';
import FeedbackDetail from '@/pages/FeedbackDetail';
import Roadmap from '@/pages/Roadmap';
import Changelog from '@/pages/Changelog';

function App() {
  const [isInitialized, setIsInitialized] = useState(false);
  const [initError, setInitError] = useState<Error | null>(null);
  const [initStatus, setInitStatus] = useState<string>('Starting initialization...');
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [welcomeUserEmail, setWelcomeUserEmail] = useState('');

  // Apply theme effect throughout the app lifecycle
  useThemeEffect();

  // Initialize app and check Supabase connection
  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Check environment variables and Supabase connection in parallel
        const [envCheck, connectionCheck] = await Promise.all([
          (async () => {
            const requiredEnvVars = {
              'VITE_SUPABASE_URL': import.meta.env.VITE_SUPABASE_URL,
              'VITE_SUPABASE_ANON_KEY': import.meta.env.VITE_SUPABASE_ANON_KEY
            };

            const missingEnvVars = Object.entries(requiredEnvVars)
              .filter(([_, value]) => !value)
              .map(([key]) => key);

            if (missingEnvVars.length > 0) {
              throw new Error(`Missing required environment variables: ${missingEnvVars.join(', ')}`);
            }
          })(),
          (async () => {
            setInitStatus('Connecting to database...');
            const { error: connectionError } = await supabase.from('displays').select('count').single();
            if (connectionError) {
              throw new Error(`Database connection error: ${connectionError.message}`);
            }
          })()
        ]);

        // Check for recent email confirmation in sessionStorage
        const emailJustConfirmed = sessionStorage.getItem('email-just-confirmed');
        const confirmedEmail = sessionStorage.getItem('confirmed-email');
        
        if (emailJustConfirmed === 'true' && confirmedEmail) {
          console.log('🔍 Found recent email confirmation in sessionStorage for:', confirmedEmail);
          // Show welcome modal for users who might have refreshed
          setTimeout(() => {
            setWelcomeUserEmail(confirmedEmail);
            setShowWelcomeModal(true);
            // Clear the flags
            sessionStorage.removeItem('email-just-confirmed');
            sessionStorage.removeItem('confirmed-email');
          }, 1000);
        }

        // App initialized successfully
        setIsInitialized(true);
        
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown initialization error';
        setInitError(error instanceof Error ? error : new Error(errorMessage));
        toast.error('Failed to initialize application', {
          description: errorMessage
        });
      }
    };

    initializeApp();
  }, []);

  useEffect(() => {
    // Handle auth success/error events
    const handleShowToast = (event: CustomEvent) => {
      const { type, title, description } = event.detail;
      if (type === 'success') {
        toast.success(title, { description });
      } else if (type === 'error') {
        toast.error(title, { description });
      }
    };

    // Handle email confirmation success
    const handleEmailConfirmed = (event: CustomEvent) => {
      const { email, isNewUser } = event.detail;
      console.log('🎉 Email confirmed event received for:', email, 'isNewUser:', isNewUser);
      
      // Set welcome modal state
      setWelcomeUserEmail(email || '');
      setShowWelcomeModal(true);
      
      // Clear any session storage flags
      sessionStorage.removeItem('email-just-confirmed');
      sessionStorage.removeItem('confirmed-email');
    };

    window.addEventListener('show-toast', handleShowToast as EventListener);
    window.addEventListener('email-confirmed', handleEmailConfirmed as EventListener);
    
    return () => {
      window.removeEventListener('show-toast', handleShowToast as EventListener);
      window.removeEventListener('email-confirmed', handleEmailConfirmed as EventListener);
    };
  }, []);

  // Network status monitoring
  useEffect(() => {
  }, []);

  if (initError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-background text-foreground">
        <div className="max-w-md w-full space-y-6 text-center">
          <h1 className="text-3xl font-bold text-red-600">Initialization Error</h1>
          <div className="space-y-4">
            <p className="text-lg font-medium">{initError.message}</p>
            <div className="bg-muted p-4 rounded-lg text-sm text-left">
              <p className="font-mono">Please check:</p>
              <ul className="list-disc list-inside space-y-2 mt-2">
                <li>Your .env file has VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY</li>
                <li>Your Supabase project is running</li>
                <li>Your network connection</li>
              </ul>
            </div>
            <pre className="bg-muted p-4 rounded-lg text-xs text-left overflow-auto">
              {JSON.stringify({ 
                url: import.meta.env.VITE_SUPABASE_URL ? 'Set' : 'Missing',
                key: import.meta.env.VITE_SUPABASE_ANON_KEY ? 'Set' : 'Missing'
              }, null, 2)}
            </pre>
          </div>
          <button
            className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            onClick={() => window.location.reload()}
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="text-lg font-medium">Loading your experience...</p>
          <p className="text-sm text-muted-foreground">{initStatus}</p>
        </div>
      </div>
    );
  }

  return (
    <AuthProvider>
      <CartProvider>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/signup" element={<CustomSignUp />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/displays" element={<DisplaysOverview />} />
            <Route path="/displays/map" element={<DisplaysMap />} />
            <Route path="/displays-search" element={<DisplaysInstantSearch />} />
            <Route path="/display/:id" element={<DisplayDetail />} />
            <Route path="/display/edit/:id?" element={<EditDisplay />} />
            <Route path="/owner/:id" element={<OwnerProfile />} />
            <Route path="/sequences" element={<Sequences />} />
            <Route path="/sequences-search" element={<SequencesInstantSearch />} />
            <Route path="/sequences/genres" element={<GenresPage />} />
            {/* Path-based filter routes */}
            <Route path="/sequences/:software" element={<Sequences />} />
            <Route path="/sequences/:software/:holiday" element={<Sequences />} />
            <Route path="/sequences/holiday/:holiday" element={<Sequences />} />
            <Route path="/sequences/genre/:genre" element={<Sequences />} />
            <Route path="/sequences/artist/:artist" element={<Sequences />} />
            <Route path="/upload-sequence" element={<UploadSequencePage />} />
            <Route path="/search" element={<Search />} />
            <Route path="/sequence/:id" element={<SequenceDetail />} />
            <Route path="/songs/:slug" element={<SongDetail />} />
            <Route path="/categories" element={<Categories />} />
            <Route path="/categories/:slug" element={<CategoryDetail />} />
            <Route path="/purchase-success/:sequenceId" element={<PurchaseSuccess />} />
            <Route path="/seller-dashboard" element={<SellerDashboard />} />
            <Route path="/profile" element={<UserProfile />} />
          <Route path="/profile/:username" element={<UserProfile />} />
            <Route path="/my-downloads" element={<MyDownloads />} />
            <Route path="/my-sequences" element={<MySequences />} />
            <Route path="/my-sequences/edit/:id" element={<EditSequence />} />
            <Route path="/videos" element={<Videos />} />
            <Route path="/videos/:id" element={<VideoDetail />} />
            <Route path="/videos/upload" element={<VideoUpload />} />
            <Route path="/feedback" element={<Feedback />} />
            <Route path="/feedback/:slug" element={<FeedbackDetail />} />
            <Route path="/roadmap" element={<Roadmap />} />
            <Route path="/changelog" element={<Changelog />} />
            <Route path="/favorites" element={<Favorites />} />
            <Route path="/test/rating" element={<RatingTest />} />
            <Route path="/test/database" element={
              <div className="container mx-auto p-8">
                <h1 className="text-2xl font-bold mb-6">Database Debug & Migration Tools</h1>
                <div className="grid gap-6">
                  <DatabaseDebugger />
                  <MigrationRunner />
                </div>
              </div>
            } />
            <Route path="/test/download" element={
              <div className="container mx-auto p-8 pt-24">
                <DownloadTestComponent />
              </div>
            } />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/getting-started" element={<GettingStarted />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/display-guide" element={<DisplayGuide />} />
            <Route path="/sequence-guide" element={<SequenceGuide />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          
          {/* Welcome Modal for newly confirmed users */}
          <WelcomeModal
            isOpen={showWelcomeModal}
            userEmail={welcomeUserEmail}
            onClose={() => setShowWelcomeModal(false)}
          />
          
          <Toaster 
            position="top-center"
            richColors
            closeButton
            expand
            duration={6000}
            toastOptions={{
              style: {
                fontSize: '16px',
                padding: '16px 20px',
                minHeight: '60px',
                borderRadius: '12px',
                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              },
              className: 'font-medium',
            }}
          />
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
